import "./chat-thread";

import { css } from "@tento-ui/component-styles";
import { Storybook } from "@tento-ui/ui/storybook";
import { ChatThemes } from "./chat-theme";
import type { ChatTheme } from "./chat-theme";
import { ChatThread } from "./chat-thread";
import type { ChatMessage } from "./chat-types";

// One conversation, every register. The stories bind the SAME messages to
// the same thread component and differ in exactly one line — the theme —
// which is the property this seam exists to prove: the messenger look, the
// document look, and the terminal look are presentation, not architecture.
//
// The themed stories carry their whole look in `ChatTheme.$style` — a real
// sheet the thread adopts — so the storybook wrapper contributes nothing but
// a size and a border. The last story composes a branded look the way an app
// does: a register spread under a name and a sheet.
//
// The escape byte in the terminal turn is spelled \u001b because a literal
// ESC survives no review tool.

const CONVERSATION: ChatMessage[] = [
  {
    id: "m1",
    role: "user",
    content: [{ type: "text", text: "run the fixture suite and show me the tail" }],
    isStreaming: false,
    createdAt: 0,
  },
  {
    id: "m2",
    role: "assistant",
    content: [
      {
        type: "text",
        text: "Running it now — 10 fixtures, the tail below is live output.",
      },
      {
        type: "terminal",
        replay: false,
        lines: [
          "$ deno test --filter term-row",
          "",
          "\u001b[32mok\u001b[0m parse is pure and repeatable",
          "\u001b[32mok\u001b[0m a diff row's ground is uniform",
          "\u001b[2m----------------------------------\u001b[0m",
          "\u001b[1m10 passed\u001b[0m \u001b[2m· 0 failed · 12ms\u001b[0m",
        ],
      },
    ],
    isStreaming: false,
    createdAt: 0,
  },
  {
    id: "m3",
    role: "user",
    content: [{ type: "text", text: "great — ship it" }],
    isStreaming: false,
    createdAt: 0,
  },
];

// The terminal look, spelled once as a sheet: ground, ink, and face as
// ordinary host CSS; the rows' metrics through the terminal block's
// documented custom-property hooks, which is how a sheet reaches shadow
// internals it cannot select.
const TUI_STYLE = css`
  :host {
    background: #002b36;
    color: #d0d0d0;
    font-family: ui-monospace, monospace;
    --chat-terminal-size: 12px;
    --chat-terminal-leading: round(nearest, 1.6em, 1px);
  }
`;

// A branded look, composed the way an app composes one: the library's
// register, the app's name and sheet. Nothing else is involved.
const PHOSPHOR: ChatTheme = {
  ...ChatThemes.tui(),
  name: "phosphor",
  $style: css`
    :host {
      background: #04140a;
      color: #49e078;
      font-family: ui-monospace, monospace;
      --chat-terminal-size: 12px;
      --chat-terminal-leading: round(nearest, 1.6em, 1px);
      --session-replay-ansi-green: #8effae;
      --session-replay-ansi-fg: #49e078;
    }
  `,
};

function $themed(theme: ChatTheme): HTMLElement {
  const $thread = ChatThread.make() as ChatThread;
  $thread.theme = theme;
  void $thread.setMessages(CONVERSATION.map((m) => ({ ...m })));
  // No padding on the thread — that is the contract. Rows carry their own
  // gutter (--chat-turn-inset) and the vertical room is the scroll
  // viewport's buffer (--thread-*-inset), so turns scroll through it.
  return $thread.assignStyles({
    display: "block",
    height: "340px",
    width: "420px",
    borderRadius: "10px",
    border: "1px solid var(--divider, #e5e5e5)",
    boxSizing: "border-box",
    "--chat-turn-inset": "12px",
    "--thread-top-inset": "12px",
    "--thread-bottom-inset": "12px",
  });
}

export class ChatThemeStoryBook extends Storybook.Default {
  title(): string {
    return "Chat Themes";
  }

  examples(): Storybook.Example[] {
    return [
      {
        // The messenger register: today's product chat, byte-for-byte.
        title: "bubbles — the messenger register",
        render: () => $themed(ChatThemes.bubbles()),
      },
      {
        // The document register: same content, no chrome, prose spacing.
        title: "plain — the document register",
        render: () => $themed(ChatThemes.plain()),
      },
      {
        // The terminal register: every turn is rows; the look rides in on
        // the theme's sheet instead of the wrapper's styles.
        title: "tui — the terminal register",
        render: () => $themed({ ...ChatThemes.tui(), $style: TUI_STYLE }),
      },
      {
        // The composition an APP performs: a register spread under a brand's
        // name and palette — the whole extension surface for a custom look.
        title: "phosphor — a composed brand look",
        render: () => $themed(PHOSPHOR),
      },
      {
        // The developer register: the same conversation as the object the
        // thread actually holds — its wire form, streaming included.
        title: "debug — the wire form",
        render: () => $themed(ChatThemes.debug()),
      },
    ];
  }
}
